import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Bus, Users, CheckCircle, XCircle, Search,
  Printer, ArrowLeft, Phone, User, Clock, Download,
  FileSpreadsheet, DollarSign, MapPin, AlertCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const calculateAge = (dob) => {
  if (!dob) return null;
  try {
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : null;
  } catch (e) {
    return null;
  }
};

const ManifestPage = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, loading: authLoading, user } = useAuth();
  const includeHistory = new URLSearchParams(location.search).get('history') === '1';

  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Cash approval modal
  const [cashModal, setCashModal] = useState(null); // { booking_ref, total_amount, paid_amount }
  const [cashAmountInput, setCashAmountInput] = useState('');
  const [cashNotes, setCashNotes] = useState('');
  const [cashSubmitting, setCashSubmitting] = useState(false);

  // Destination arrival
  const [arrivalSubmitting, setArrivalSubmitting] = useState(null); // booking_ref

  // Check-in
  const [checkinSubmitting, setCheckinSubmitting] = useState(null); // booking_ref
  const [noShowSubmitting, setNoShowSubmitting] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleTripId, setRescheduleTripId] = useState('');
  const [rescheduleSeats, setRescheduleSeats] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  const [allTrips, setAllTrips] = useState([]);

  const fetchAllTrips = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/trips`);
      setAllTrips(res.data || []);
    } catch (e) {
      console.error('Failed to fetch trips list:', e);
    }
  }, []);

  const fetchManifest = useCallback(async () => {
    if (!tripId) {
      setLoading(false);
      return;
    }
    try {
      const historyQuery = includeHistory ? '?include_history=true' : '';
      const response = await axios.get(`${API_URL}/trips/${tripId}/manifest${historyQuery}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManifest(response.data);
    } catch (error) {
      console.error('Failed to fetch manifest:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('You are not assigned to this trip. You can only view and operate on your assigned trips.');
        if (user?.role === 'driver') {
          navigate('/driver');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load manifest');
      }
    } finally {
      setLoading(false);
    }
  }, [includeHistory, token, tripId, user, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    fetchManifest();
    fetchAllTrips();
  }, [tripId, includeHistory, token, authLoading, fetchManifest, fetchAllTrips]);

  const handlePrint = () => {
    exportToPDF();
  };

  const exportToCSV = () => {
    if (!manifest?.passengers?.length) {
      toast.error('No passengers to export');
      return;
    }

    const headers = ['#', 'Ticket #', 'Name', 'Sex', 'Age', 'Passport Number', 'Education', 'Occupation', 'Boarding Point', 'Drop-off Point', 'Phone', 'Payment Method', 'Payment Status', 'Status'];
    const rows = manifest.passengers.map((p, idx) => {
      const pAge = p.age != null && p.age !== 'N/A' ? p.age : (calculateAge(p.date_of_birth) || 'N/A');
      const passNo = p.passport_number || (p.is_local ? 'Local (Exempt)' : 'N/A');
      return [
        idx + 1,
        p.ticket_number || p.booking_ref || '',
        p.full_name || p.name || '',
        p.sex || 'M',
        pAge,
        passNo,
        p.education || 'Secondary',
        p.occupation || 'General',
        p.boarding_point || 'Harare',
        p.dropoff_point || 'Durban',
        p.phone || '',
        p.payment_method || 'cash',
        p.payment_status || '',
        p.status || ''
      ];
    });

    const csv = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manifest_${manifest.trip_number || tripId}_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Manifest exported to CSV');
  };

  const exportToPDF = () => {
    if (!manifest?.passengers?.length) {
      toast.error('No passengers to export');
      return;
    }

    const tripNum = manifest.trip_number || 'N/A';
    const routeName = manifest.route || 'N/A';
    const viaName = manifest.via ? ` via ${manifest.via}` : '';
    const depDate = manifest.departure
      ? format(parseISO(manifest.departure), 'dd MMM yyyy HH:mm')
      : 'N/A';
    const busReg = manifest.bus_registration || 'ADF 2026';
    const driverName = (manifest.driver_name && manifest.driver_name !== 'N/A' && manifest.driver_name !== 'null') ? manifest.driver_name : 'Takudzwa Kuwenyi (Driver)';
    const conductorName = (manifest.conductor_name && manifest.conductor_name !== 'N/A' && manifest.conductor_name !== 'null') ? manifest.conductor_name : 'Chipo Chiduku (Host)';
    const conductorPhone = manifest.conductor_phone || '+263 786 563 430';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Passenger Manifest - ${tripNum}</title>
        <style>
          @page { size: A4 landscape; margin: 7mm; }
          * { box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; margin: 0; padding: 0; font-size: 9px; color: #111; background: #fff; }
          h1 { color: #8B0000; margin: 0 0 4px 0; font-size: 20px; font-weight: 800; letter-spacing: 0.5px; }
          h2 { color: #555; margin: 0; font-size: 13px; font-weight: 600; }
          table { width: 100%; table-layout: fixed; border-collapse: collapse; margin-top: 10px; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; page-break-inside: avoid; }
          th, td { border: 1px solid #cbd5e1; padding: 4px 3px; text-align: left; font-size: 8px; line-height: 1.25; vertical-align: top; overflow-wrap: anywhere; }
          th { background: #8B0000; color: white; font-weight: bold; }
          th:nth-child(1), td:nth-child(1) { width: 3%; text-align: center; }
          th:nth-child(2), td:nth-child(2) { width: 9%; font-weight: bold; }
          th:nth-child(3), td:nth-child(3) { width: 15%; font-weight: 600; }
          th:nth-child(4), td:nth-child(4) { width: 4%; text-align: center; }
          th:nth-child(5), td:nth-child(5) { width: 4%; text-align: center; }
          th:nth-child(6), td:nth-child(6) { width: 11%; }
          th:nth-child(7), td:nth-child(7) { width: 8%; }
          th:nth-child(8), td:nth-child(8) { width: 8%; }
          th:nth-child(9), td:nth-child(9) { width: 11%; }
          th:nth-child(10), td:nth-child(10) { width: 11%; }
          th:nth-child(11), td:nth-child(11) { width: 16%; }
          tr:nth-child(even) { background: #f8fafc; }
          .header { display: flex; justify-content: space-between; gap: 16px; border-bottom: 2px solid #8B0000; padding: 12px 16px 8px 16px; }
          .header > div:last-child { max-width: 55%; font-size: 9px; line-height: 1.4; }
          .header p { margin: 2px 0; }
          .content-wrap { padding: 0 16px 16px 16px; }
          .footer { margin-top: 14px; text-align: center; font-size: 8px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 8px; }
          
          /* Non-printing interactive control toolbar */
          .action-toolbar {
            background: #0f172a;
            color: #fff;
            padding: 10px 18px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            position: sticky;
            top: 0;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0,0,0,0.25);
            font-family: sans-serif;
          }
          .action-toolbar .title {
            font-size: 13px;
            font-weight: 700;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .action-toolbar .btn-group {
            display: flex;
            gap: 10px;
          }
          .action-toolbar button {
            cursor: pointer;
            border: none;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 700;
            padding: 7px 16px;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: opacity 0.2s;
          }
          .action-toolbar button:hover { opacity: 0.9; }
          .btn-print { background: #8B0000; color: #fff; }
          .btn-close { background: #334155; color: #cbd5e1; }

          @media print {
            .action-toolbar { display: none !important; }
            .header { padding-left: 0; padding-right: 0; }
            .content-wrap { padding: 0; }
            html, body { width: 100%; margin: 0; padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="action-toolbar">
          <div class="title">
            <span>🚌 Panashe Coaches — Passenger Manifest: <strong>${tripNum}</strong> (${manifest.passengers.length} Passengers)</span>
          </div>
          <div class="btn-group">
            <button class="btn-print" onclick="window.print()">
              🖨️ Save as PDF / Print
            </button>
            <button class="btn-close" onclick="window.close()">
              ✕ Close Window
            </button>
          </div>
        </div>

        <div class="content-wrap">
          <div class="header">
            <div>
              <h1>PANASHE COACHES</h1>
              <h2>Cross-Border & Regional Passenger Manifest</h2>
            </div>
            <div style="text-align: right;">
              <p><strong>Trip Number:</strong> ${tripNum}</p>
              <p><strong>Route:</strong> ${routeName}${viaName}</p>
              <p><strong>Departure Date:</strong> ${depDate}</p>
              <p><strong>Bus Registration:</strong> ${busReg}</p>
              <p><strong>Driver:</strong> ${driverName}</p>
              <p><strong>Conductor / Host:</strong> ${conductorName} (${conductorPhone})</p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Ticket #</th>
                <th>Passenger Name</th>
                <th>Sex</th>
                <th>Age</th>
                <th>Passport / ID No</th>
                <th>Education</th>
                <th>Occupation</th>
                <th>Boarding Point</th>
                <th>Drop-off Point</th>
                <th>Phone / Contact</th>
              </tr>
            </thead>
            <tbody>
              ${manifest.passengers.map((p, idx) => {
                const pAge = p.age != null && p.age !== 'N/A' ? p.age : (calculateAge(p.date_of_birth) || 'N/A');
                const passNo = p.passport_number || (p.is_local ? 'Local (Exempt)' : 'N/A');
                return `
                <tr>
                  <td>${idx + 1}</td>
                  <td><strong>${p.ticket_number || p.booking_ref || ''}</strong></td>
                  <td>${p.full_name || p.name || ''}</td>
                  <td>${p.sex || 'M'}</td>
                  <td>${pAge}</td>
                  <td>${passNo}</td>
                  <td>${p.education || 'Secondary'}</td>
                  <td>${p.occupation || 'General'}</td>
                  <td>${p.boarding_point || 'Harare'}</td>
                  <td>${p.dropoff_point || 'Durban'}</td>
                  <td>${p.phone || ''}</td>
                </tr>
              `;
              }).join('')}
            </tbody>
          </table>
          <div class="footer">
            <p><strong>Total Passengers:</strong> ${manifest.passengers.length} | <strong>Manifest Generated:</strong> ${format(new Date(), 'dd MMM yyyy HH:mm')}</p>
            <p>Panashe Coaches Operations • 24/7 Support: +263 78 656 3430 | +263 77 727 5154 • www.panashecoaches.com</p>
          </div>
        </div>

        <script>
          // Automatically trigger print dialog in this isolated process without locking parent tab
          window.addEventListener('load', function() {
            setTimeout(function() {
              try { window.print(); } catch (e) {}
            }, 300);
          });
        </script>
      </body>
      </html>
    `;

    try {
      const blob = new Blob([printContent], { type: 'text/html;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      const newWin = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!newWin) {
        toast.error('Pop-up was blocked. Please allow pop-ups for this site to view the manifest PDF.');
        URL.revokeObjectURL(blobUrl);
        return;
      }
      toast.success('Manifest PDF preview opened in a safe isolated tab.');
      // Revoke the blob URL after 2 minutes to free memory
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch (e) {}
      }, 120000);
    } catch (err) {
      console.error('Failed to open manifest PDF:', err);
      toast.error('Failed to generate PDF view');
    }
  };

  // ===================== CASH APPROVAL =====================
  const openCashModal = (passenger) => {
    setCashModal({
      booking_ref: passenger.booking_ref,
      total_amount: passenger.total_amount || 0,
      paid_amount: passenger.paid_amount || 0,
      currency: passenger.currency || 'USD',
      pay_forward_currency: passenger.pay_forward_currency || passenger.currency || 'USD',
      pay_forward_balance_alt: passenger.pay_forward_balance_alt || 0
    });
    const balance = (passenger.total_amount || 0) - (passenger.paid_amount || 0);
    setCashAmountInput(balance > 0 ? String(balance.toFixed(2)) : '');
    setCashNotes('');
  };

  const handleApproveCash = async () => {
    if (!cashAmountInput || isNaN(parseFloat(cashAmountInput)) || parseFloat(cashAmountInput) <= 0) {
      toast.error('Please enter a valid amount received');
      return;
    }
    setCashSubmitting(true);
    const targetRef = cashModal.booking_ref;
    try {
      const res = await axios.post(
        `${API_URL}/bookings/${targetRef}/approve-cash`,
        { amount_paid: parseFloat(cashAmountInput), payment_currency: cashModal.currency, notes: cashNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Top-up recorded! SMS confirmation sent to passenger.');
      setCashModal(null);
      fetchManifest();

      // Mandatory reprint prompt to host / conductor / staff
      toast(
        (t) => (
          <div className="flex flex-col gap-2 p-1">
            <p className="font-bold text-slate-900 text-sm">🖨️ ACTION REQUIRED: Reprint Ticket</p>
            <p className="text-xs text-slate-600">
              Payment approved! The passenger ticket status is updated to CONFIRMED. The ticket must be reprinted before boarding.
            </p>
            <div className="flex gap-2 mt-1">
              <Button
                size="sm"
                className="btn-panashe text-xs py-1 px-3"
                onClick={() => {
                  toast.dismiss(t.id);
                  window.open(`/ticket/${targetRef}`, '_blank');
                }}
              >
                <Printer className="h-3.5 w-3.5 mr-1" />
                Print / Reprint Ticket
              </Button>
              <Button size="sm" variant="outline" className="text-xs py-1" onClick={() => toast.dismiss(t.id)}>
                Dismiss
              </Button>
            </div>
          </div>
        ),
        { duration: 15000 }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record cash payment');
    } finally {
      setCashSubmitting(false);
    }
  };

  // ===================== REACH DESTINATION =====================
  const handleReachDestination = async (booking_ref) => {
    if (!window.confirm(`Mark passenger (${booking_ref}) as arrived at destination? Their seats will be released.`)) return;
    setArrivalSubmitting(booking_ref);
    try {
      const res = await axios.post(
        `${API_URL}/bookings/${booking_ref}/reach-destination`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Passenger marked as arrived. Seat released!');
      fetchManifest();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update arrival status');
    } finally {
      setArrivalSubmitting(null);
    }
  };

  // ===================== CHECK-IN =====================
  const handleCheckIn = async (booking_ref) => {
    setCheckinSubmitting(booking_ref);
    try {
      const res = await axios.post(
        `${API_URL}/bookings/${booking_ref}/checkin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.already_checked_in) {
        toast.info('Passenger was already checked in.');
      } else {
        toast.success('✅ Passenger checked in and on board!');
      }
      fetchManifest();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckinSubmitting(null);
    }
  };

  const handleMarkNoShow = async (booking_ref) => {
    if (!window.confirm(`Mark booking ${booking_ref} as a no-show?`)) return;
    setNoShowSubmitting(booking_ref);
    try {
      const response = await axios.post(
        `${API_URL}/bookings/${booking_ref}/no-show`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Passenger marked as no-show');
      fetchManifest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not mark passenger as no-show');
    } finally {
      setNoShowSubmitting(null);
    }
  };

  const openReschedule = (passenger) => {
    const bookingSeats = manifest.passengers
      .filter(item => item.booking_ref === passenger.booking_ref)
      .map(item => item.seat)
      .filter(Boolean);
    setRescheduleTarget(passenger);
    setRescheduleTripId('');
    setRescheduleSeats(bookingSeats.join(', '));
  };

  const submitReschedule = async () => {
    const seatNumbers = rescheduleSeats.split(',').map(seat => seat.trim().toUpperCase()).filter(Boolean);
    if (!rescheduleTripId || !seatNumbers.length) {
      toast.error('Choose a new trip and enter the seat numbers');
      return;
    }
    setRescheduling(true);
    try {
      const response = await axios.post(
        `${API_URL}/bookings/${rescheduleTarget.booking_ref}/reschedule`,
        { trip_id: rescheduleTripId, seat_numbers: seatNumbers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const delta = Number(response.data.fare_delta || 0);
      toast.success(delta > 0
        ? `Rescheduled. Additional balance due: ${rescheduleTarget.currency || 'USD'} ${delta.toFixed(2)}`
        : delta < 0
          ? `Rescheduled. Fare credit: ${rescheduleTarget.currency || 'USD'} ${Math.abs(delta).toFixed(2)}`
          : 'Booking rescheduled with no fare change');
      setRescheduleTarget(null);
      fetchManifest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reschedule booking');
    } finally {
      setRescheduling(false);
    }
  };

  const filteredPassengers = manifest?.passengers?.filter(p =>
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.phone || '').includes(searchTerm) ||
    (p.seat || '').includes(searchTerm) ||
    (p.booking_ref || '').toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-slate-200 rounded-lg" />
          <div className="h-96 bg-slate-200 rounded-lg" />
        </div>
      </DashboardLayout>
    );
  }

  if (!manifest) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Bus className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold mb-2">Manifest Not Found</h2>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 no-print">
          <div className="flex items-center gap-4 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                Trip Manifest
              </h1>
              <p className="text-slate-600">{manifest.route}</p>
              {manifest.history_mode && (
                <p className="mt-1 text-xs font-medium text-amber-800">
                  Historical manifest includes cancelled and refunded bookings.
                </p>
              )}
            </div>

            {/* Trip Selector Dropdown */}
            {allTrips.length > 0 && (
              <div className="flex items-center gap-2 ml-0 md:ml-4 bg-slate-100 p-2 rounded-lg border border-slate-200">
                <Bus className="h-4 w-4 text-panashe-blue flex-shrink-0" />
                <span className="text-xs font-bold text-slate-600 uppercase">Select Trip:</span>
                <select
                  className="bg-white border border-slate-300 rounded px-2.5 py-1 text-sm font-medium text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-panashe-blue cursor-pointer"
                  value={tripId}
                  onChange={(e) => navigate(`/dashboard/manifest/${e.target.value}${includeHistory ? '?history=1' : ''}`)}
                >
                  {allTrips.map(t => {
                    const departureStr = t.departure_datetime ? format(parseISO(t.departure_datetime), 'dd MMM yyyy HH:mm') : '';
                    const bookedSeats = t.total_seats - t.available_seats;
                    return (
                      <option key={t.id} value={t.id}>
                        {t.trip_number ? `${t.trip_number} — ` : ''}{t.origin} → {t.destination} ({departureStr}) [{bookedSeats} booked]
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel/CSV
            </Button>
            <Button variant="outline" onClick={exportToPDF} data-testid="export-pdf-btn">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button onClick={handlePrint} className="btn-panashe" data-testid="print-manifest-btn">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* Trip Summary */}
        <Card className="card-panashe">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-panashe-blue/10 rounded-lg flex items-center justify-center">
                  <Clock className="h-5 w-5 text-panashe-blue" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Departure</p>
                  <p className="font-heading font-bold">
                    {format(parseISO(manifest.departure), 'HH:mm')}
                  </p>
                  <p className="text-xs text-slate-400">
                    {format(parseISO(manifest.departure), 'dd MMM yyyy')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-panashe-gold/20 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-panashe-gold" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Passengers</p>
                  <p className="font-heading font-bold">{manifest.total_passengers}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Checked In</p>
                  <p className="font-heading font-bold text-green-600">{manifest.checked_in}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="font-heading font-bold text-yellow-600">
                    {manifest.total_passengers - manifest.checked_in}
                  </p>
                </div>
              </div>
            </div>

            {/* Trip Crew Info Bar */}
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <span className="text-slate-500 font-medium">Driver: </span>
                  <span className="font-bold text-slate-800">{manifest.driver_name && manifest.driver_name !== 'N/A' && manifest.driver_name !== 'null' ? manifest.driver_name : 'Takudzwa Kuwenyi (Driver)'}</span>
                </div>
                <div>
                  <span className="text-slate-500 font-medium">Host: </span>
                  <span className="font-bold text-emerald-800">{manifest.conductor_name && manifest.conductor_name !== 'N/A' && manifest.conductor_name !== 'null' ? manifest.conductor_name : 'Chipo Chiduku (Host)'}</span>
                </div>
                {manifest.conductor_phone && (
                  <div>
                    <span className="text-slate-500 font-medium">Host Phone: </span>
                    <span className="font-mono text-slate-700">{manifest.conductor_phone}</span>
                  </div>
                )}
              </div>
              <div className="text-slate-500">
                <span>Bus: </span>
                <span className="font-mono font-bold text-slate-800">{manifest.bus_registration || 'ADF 2026'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative no-print">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, phone, seat, or booking ref..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="manifest-search"
          />
        </div>

        {/* Passenger List */}
        <Card className="card-panashe">
          <CardHeader className="no-print">
            <CardTitle className="font-heading flex items-center gap-2">
              <Users className="h-5 w-5" />
              Passenger List
              <Badge variant="outline">{filteredPassengers.length} passengers</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Mobile View: Touch-Friendly Passenger Cards (Phones & Small screens) */}
            <div className="block md:hidden divide-y divide-slate-200">
              {filteredPassengers.map((passenger, idx) => {
                const isZeroFare = (passenger.total_amount || 0) <= 0;
                const isCashPending =
                  !isZeroFare && (
                    passenger.payment_status === 'pending' ||
                    passenger.payment_status === 'partial'
                  );
                const isArrived = passenger.status === 'completed';
                const isCheckedIn = passenger.checked_in === true;
                const isPaymentApproved = passenger.payment_status === 'paid' || isZeroFare;
                const canCheckIn = isPaymentApproved && !isCheckedIn && !isArrived;
                const departurePassed = manifest?.departure && new Date(manifest.departure).getTime() <= Date.now();
                const canReschedule = !departurePassed && !passenger.boarded && !isCheckedIn && !['cancelled', 'completed', 'refunded', 'no_show'].includes(passenger.status);
                const isNoShow = passenger.status === 'no_show';
                const canMarkNoShow = departurePassed && !passenger.boarded && !isArrived && !isNoShow;
                const canMarkArrived = isCheckedIn && isPaymentApproved;

                return (
                  <div key={`mob-${passenger.booking_ref || idx}`} className={`p-4 space-y-3 ${isCashPending ? 'bg-amber-50/60' : 'bg-white'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-bold text-base text-slate-900 truncate">
                            {passenger.full_name || passenger.name}
                          </span>
                          <span className="text-xs bg-blue-100 text-panashe-blue px-2 py-0.5 rounded font-mono font-bold">
                            {passenger.seat ? `Seat ${passenger.seat}` : 'No Seat'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 font-mono mt-0.5">
                          Ticket: <strong className="text-slate-700">{passenger.ticket_number || passenger.booking_ref}</strong>
                        </p>
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-1">
                        {isCheckedIn ? (
                          <Badge className="bg-green-100 text-green-800 border-green-300 font-bold text-[11px]">
                            ● Checked In
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-800 border-amber-300 font-medium text-[11px]">
                            Pending Boarding
                          </Badge>
                        )}
                        {isArrived && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px]">
                            Arrived ✓
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Route</span>
                        <span className="font-bold text-slate-800">
                          {passenger.boarding_point || 'Harare'} → {passenger.dropoff_point || 'Durban'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Phone</span>
                        <a href={`tel:${passenger.phone}`} className="font-medium text-panashe-blue">
                          {passenger.phone || 'N/A'}
                        </a>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Passport / ID</span>
                        <span className="font-mono text-slate-700">
                          {passenger.passport_number || (passenger.is_local ? 'Local (Exempt)' : 'N/A')}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Payment</span>
                        <span className={`font-bold ${isPaymentApproved ? 'text-green-700' : 'text-amber-700'}`}>
                          {passenger.currency || 'USD'} {passenger.paid_amount || 0}/{passenger.total_amount || 0}
                          <span className="text-[10px] ml-1 uppercase">({passenger.payment_status || 'unpaid'})</span>
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      {isCashPending && (
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white font-bold flex-1 text-xs h-9"
                          onClick={() => openCashModal(passenger)}
                        >
                          Approve Cash
                        </Button>
                      )}
                      {canCheckIn && (
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold flex-1 text-xs h-9"
                          onClick={() => handleCheckIn(passenger.booking_ref)}
                          disabled={checkinSubmitting === passenger.booking_ref}
                        >
                          {checkinSubmitting === passenger.booking_ref ? 'Checking in...' : 'Check In'}
                        </Button>
                      )}
                      {canMarkArrived && !isArrived && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs h-9"
                          onClick={() => handleReachDestination(passenger.booking_ref)}
                          disabled={arrivalSubmitting === passenger.booking_ref}
                        >
                          {arrivalSubmitting === passenger.booking_ref ? '...' : 'Mark Arrived'}
                        </Button>
                      )}
                      {canReschedule && !isArrived && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs h-9"
                          onClick={() => openReschedule(passenger)}
                        >
                          Reschedule
                        </Button>
                      )}
                      {isPaymentApproved && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-9 border-slate-300"
                          onClick={() => window.open(`/ticket/${passenger.booking_ref}`, '_blank')}
                        >
                          <Printer className="h-3 w-3 mr-1" /> Ticket
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View (Tablets & Desktops) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ticket #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Passenger</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Sex / Age</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Passport No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Education & Occupation</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Boarding / Drop-off</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Payment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider no-print">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredPassengers.map((passenger, idx) => {
                    const isZeroFare = (passenger.total_amount || 0) <= 0;
                    const isCashPending =
                      !isZeroFare && (
                        passenger.payment_status === 'pending' ||
                        passenger.payment_status === 'partial'
                      );
                    const isArrived = passenger.status === 'completed';
                    const isCheckedIn = passenger.checked_in === true;
                    // Payment is strictly approved ONLY when payment_status is paid (or zero-fare)
                    const isPaymentApproved = passenger.payment_status === 'paid' || isZeroFare;
                    // Can check in if payment approved and not yet checked in and not arrived
                    const canCheckIn = isPaymentApproved && !isCheckedIn && !isArrived;
                    const departurePassed = manifest?.departure && new Date(manifest.departure).getTime() <= Date.now();
                    const canReschedule = !departurePassed && !passenger.boarded && !isCheckedIn && !['cancelled', 'completed', 'refunded', 'no_show'].includes(passenger.status);
                    const isNoShow = passenger.status === 'no_show';
                    const canMarkNoShow = departurePassed && !passenger.boarded && !isArrived && !isNoShow;
                    // Can only mark arrived if BOTH checked in AND payment approved
                    const canMarkArrived = isCheckedIn && isPaymentApproved;
                    const arrivedTooltip = !isPaymentApproved
                      ? 'Payment must be approved before marking as arrived'
                      : !isCheckedIn
                      ? 'Passenger must be checked in before marking as arrived'
                      : 'Mark passenger as arrived at destination';

                    return (
                      <tr
                        key={idx}
                        className={`hover:bg-slate-50 ${isCashPending ? 'bg-amber-50' : ''}`}
                        data-testid={`passenger-row-${idx}`}
                      >
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="font-mono bg-blue-50 text-panashe-blue border-blue-200">
                            {passenger.ticket_number || passenger.booking_ref}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-panashe-blue/10 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-panashe-blue" />
                            </div>
                            <div>
                              <span className="font-medium block">{passenger.name}</span>
                              {passenger.passenger_type && passenger.passenger_type !== 'Standard' && (
                                <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded font-medium">
                                  {passenger.passenger_type}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="font-semibold text-slate-700">{passenger.sex || 'M'}</span>
                          <span className="text-slate-400 mx-1">•</span>
                          <span className="text-slate-600">
                            {(() => {
                              const a = passenger.age != null && passenger.age !== 'N/A' ? passenger.age : calculateAge(passenger.date_of_birth);
                              return a != null ? `${a} yrs` : 'N/A';
                            })()}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">
                          {passenger.passport_number || (passenger.is_local ? 'Local (Exempt)' : 'N/A')}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          <div className="font-medium text-slate-800">{passenger.education || 'Secondary'}</div>
                          <div className="text-slate-500">{passenger.occupation || 'General'}</div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <div className="font-semibold text-emerald-700">{passenger.boarding_point || 'Harare'}</div>
                          <div className="text-slate-500">→ {passenger.dropoff_point || 'Durban'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Phone className="h-3 w-3" />
                            {passenger.phone}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1 items-start">
                            {/* Payment Method Badge */}
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-800 border border-slate-200">
                              {passenger.payment_method === 'ecocash' ? (
                                <>📱 EcoCash</>
                              ) : passenger.payment_method === 'card' ? (
                                <>💳 Card / Paynow</>
                              ) : passenger.payment_method === 'onemoney' ? (
                                <>📱 OneMoney</>
                              ) : (
                                <>💵 Cash</>
                              )}
                            </span>

                            {/* Payment Status Badge */}
                            <Badge
                              className={
                                passenger.payment_status === 'paid'
                                  ? 'bg-green-100 text-green-700'
                                  : passenger.payment_status === 'partial'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-red-100 text-red-700'
                              }
                            >
                              {passenger.payment_status}
                            </Badge>

                            {/* Payment Amounts */}
                            {passenger.paid_amount != null && passenger.total_amount != null && (
                              <span className="text-xs text-slate-500">
                                {passenger.currency || 'USD'} {Number(passenger.paid_amount).toFixed(2)} / {Number(passenger.total_amount).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {isArrived ? (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-200">
                              <MapPin className="h-3 w-3 mr-1" />
                              Arrived at Destination
                            </Badge>
                          ) : passenger.checked_in ? (
                            <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Checked In (On Bus)
                            </Badge>
                          ) : isPaymentApproved ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Confirmed (Awaiting Check-in)
                            </Badge>
                          ) : isCashPending ? (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Pending Cash Payment
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 no-print">
                          <div className="flex flex-col gap-1">
                            {/* Approve Cash — hidden for zero-fare (under 5 / complimentary) passengers */}
                            {!isZeroFare && passenger.payment_status !== 'paid' && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                onClick={() => openCashModal(passenger)}
                                data-testid={`approve-cash-${passenger.booking_ref}`}
                              >
                                <DollarSign className="h-3 w-3 mr-1" />
                                {passenger.paid_amount > 0 ? 'Top Up Ticket' : 'Record Cash'}
                              </Button>
                            )}

                            {/* Check-In button — available when payment is confirmed */}
                            {!isArrived && (
                              <Button
                                size="sm"
                                className={`text-xs font-semibold ${
                                  canCheckIn
                                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                    : 'border border-slate-200 text-slate-400 cursor-not-allowed opacity-50 bg-white'
                                }`}
                                onClick={() => canCheckIn && handleCheckIn(passenger.booking_ref)}
                                disabled={checkinSubmitting === passenger.booking_ref || isCheckedIn || !canCheckIn}
                                title={
                                  isCheckedIn ? 'Already checked in' :
                                  !isPaymentApproved ? 'Approve payment first' :
                                  'Check in this passenger'
                                }
                                data-testid={`checkin-${passenger.booking_ref}`}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {checkinSubmitting === passenger.booking_ref
                                  ? 'Checking in...'
                                  : isCheckedIn
                                  ? 'Checked In ✓'
                                  : 'Check In'}
                              </Button>
                            )}

                            {!isArrived && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs"
                                onClick={() => canReschedule && openReschedule(passenger)}
                                disabled={!canReschedule}
                                title="Move this booking to another upcoming trip"
                                data-testid={`reschedule-${passenger.booking_ref}`}
                              >
                                <Bus className="h-3 w-3 mr-1" /> Reschedule
                              </Button>
                            )}

                            {!isArrived && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-300 text-amber-800 hover:bg-amber-50 text-xs"
                                onClick={() => canMarkNoShow && handleMarkNoShow(passenger.booking_ref)}
                                disabled={noShowSubmitting === passenger.booking_ref || (!canMarkNoShow && !isNoShow)}
                                title={isNoShow ? 'Passenger marked as no-show' : 'Available after scheduled departure for unboarded passengers'}
                                data-testid={`no-show-${passenger.booking_ref}`}
                              >
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {noShowSubmitting === passenger.booking_ref ? 'Updating...' : isNoShow ? 'No-show' : 'Mark No-show'}
                              </Button>
                            )}

                            {/* Reached Destination — requires check-in AND payment approved */}
                            {!isArrived && (
                              <Button
                                size="sm"
                                variant="outline"
                                className={`text-xs ${
                                  canMarkArrived
                                    ? 'border-blue-300 text-blue-700 hover:bg-blue-50'
                                    : 'border-slate-200 text-slate-400 cursor-not-allowed opacity-50'
                                }`}
                                onClick={() => canMarkArrived && handleReachDestination(passenger.booking_ref)}
                                disabled={arrivalSubmitting === passenger.booking_ref || !canMarkArrived}
                                title={arrivedTooltip}
                                data-testid={`arrived-${passenger.booking_ref}`}
                              >
                                <MapPin className="h-3 w-3 mr-1" />
                                {arrivalSubmitting === passenger.booking_ref ? 'Updating...' : 'Arrived'}
                              </Button>
                            )}

                            {/* Print Ticket — available when payment approved */}
                            {isPaymentApproved ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs"
                                onClick={() => window.open(`/ticket/${passenger.booking_ref}`, '_blank')}
                                data-testid={`print-ticket-${passenger.booking_ref}`}
                              >
                                <Printer className="h-3 w-3 mr-1" />
                                Print Ticket
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-slate-200 text-slate-400 cursor-not-allowed opacity-50 text-xs"
                                disabled
                                title="Cash payment must be approved before printing ticket"
                                data-testid={`print-ticket-${passenger.booking_ref}`}
                              >
                                <Printer className="h-3 w-3 mr-1" />
                                Print Ticket
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredPassengers.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Users className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                  <p>No passengers found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ======= CASH APPROVAL MODAL ======= */}
      {cashModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h2 className="font-heading text-xl font-bold mb-1 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              {cashModal.paid_amount > 0 ? 'Top Up Passenger Ticket' : 'Record Cash Payment'}
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Booking: <span className="font-mono text-panashe-blue">{cashModal.booking_ref}</span>
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Total Fare:</span>
                <span className="font-bold">{cashModal.currency} {Number(cashModal.total_amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Already Paid:</span>
                <span className="font-bold text-green-700">{cashModal.currency} {Number(cashModal.paid_amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t border-amber-300 mt-2 pt-2">
                <span className="text-slate-700 font-semibold">Balance Due:</span>
                <div className="text-right">
                  <span className="font-bold text-red-600">
                    {cashModal.currency} {Math.max(0, cashModal.total_amount - cashModal.paid_amount).toFixed(2)}
                  </span>
                  {cashModal.pay_forward_currency && cashModal.pay_forward_currency !== cashModal.currency && (
                    <p className="text-xs text-slate-600 font-semibold mt-0.5">
                      ({cashModal.pay_forward_currency === 'ZAR' ? 'R' : '$'}
                      {(Math.max(0, cashModal.total_amount - cashModal.paid_amount) * (cashModal.pay_forward_currency === 'ZAR' ? 18.5 : (1 / 18.5))).toFixed(2)} {cashModal.pay_forward_currency})
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="cash-amount">Amount Received Now ({cashModal.currency})</Label>
                <Input
                  id="cash-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="e.g. 80.00"
                  value={cashAmountInput}
                  onChange={e => setCashAmountInput(e.target.value)}
                  className="mt-1"
                  autoFocus
                />
                {parseFloat(cashAmountInput) > 0 && parseFloat(cashAmountInput) < (cashModal.total_amount - cashModal.paid_amount) && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ This is a partial payment — ticket will remain <strong>PENDING</strong> until fully paid.
                  </p>
                )}
                {parseFloat(cashAmountInput) >= (cashModal.total_amount - cashModal.paid_amount) && cashAmountInput !== '' && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ Full payment — ticket will be <strong>CONFIRMED</strong>.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="cash-notes">Notes (optional)</Label>
                <Input
                  id="cash-notes"
                  placeholder="e.g. Paid at Harare depot"
                  value={cashNotes}
                  onChange={e => setCashNotes(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setCashModal(null)}
                disabled={cashSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleApproveCash}
                disabled={cashSubmitting}
              >
                {cashSubmitting ? 'Saving...' : 'Confirm Payment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {rescheduleTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-md">
            <h2 className="font-heading text-xl font-bold mb-1">Reschedule Ticket</h2>
            <p className="text-sm text-slate-500 mb-5">
              Booking <span className="font-mono text-panashe-blue">{rescheduleTarget.booking_ref}</span>
            </p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="reschedule-trip">New trip</Label>
                <select
                  id="reschedule-trip"
                  className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  value={rescheduleTripId}
                  onChange={event => setRescheduleTripId(event.target.value)}
                >
                  <option value="">Select an upcoming trip</option>
                  {allTrips.filter(trip => trip.id !== tripId && trip.status === 'scheduled' && new Date(trip.departure_datetime).getTime() > Date.now()).map(trip => (
                    <option key={trip.id} value={trip.id}>
                      {trip.trip_number || trip.id} · {trip.origin} to {trip.destination} · {trip.departure_datetime ? format(parseISO(trip.departure_datetime), 'dd MMM yyyy HH:mm') : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="reschedule-seats">Seat numbers</Label>
                <Input
                  id="reschedule-seats"
                  value={rescheduleSeats}
                  onChange={event => setRescheduleSeats(event.target.value)}
                  placeholder="A1, A2"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setRescheduleTarget(null)} disabled={rescheduling}>Close</Button>
              <Button className="flex-1 btn-panashe" onClick={submitReschedule} disabled={rescheduling}>
                {rescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ManifestPage;
